import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  TablePagination,
} from '@mui/material';
import { Add, KeyboardReturn } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import LoadingLogo from '../../../components/LoadingLogo';
import EstadoChip from '../components/EstadoChip';
import DevolucionFormModal from '../components/DevolucionFormModal';
import sigcodService from '../services/sigcodService';

const RETURN_ESTADOS = ['', 'SOLICITADA', 'APROBADA', 'EN_PROCESO', 'RECIBIDA', 'CERRADA', 'RECHAZADA'];

const DevolucionesPage = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [estado, setEstado] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, limit: rowsPerPage };
      if (estado) params.estado = estado;
      const res = await sigcodService.getReturns(params);
      setReturns(res.data || []);
      setTotal(res.pagination?.total || 0);
    } catch {
      setSnackbar({ open: true, message: 'Error al cargar devoluciones', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, rowsPerPage, estado]);

  const handleCreate = async (data) => {
    await sigcodService.createReturn(data);
    setSnackbar({ open: true, message: 'Devolucion creada exitosamente', severity: 'success' });
    loadData();
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

  if (loading && returns.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LoadingLogo size={120} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Devoluciones SIGCOD"
        subtitle="Gestion de devoluciones documentales"
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpenModal(true)}>
            Nueva Devolucion
          </Button>
        }
      />

      {/* Filter */}
      <Card elevation={0} sx={{ mb: 2, borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)' }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={estado}
                onChange={(e) => { setEstado(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Todos</MenuItem>
                {RETURN_ESTADOS.filter(Boolean).map((e) => (
                  <MenuItem key={e} value={e}>{e}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(29,31,32,0.16)', boxShadow: '0 1px 2px rgba(43,43,45,.14)' }}>
        <CardContent>
          {returns.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <KeyboardReturn sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay devoluciones
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={() => setOpenModal(true)}>
                Crear Primera Devolucion
              </Button>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Consecutivo</TableCell>
                      <TableCell>Solicitud</TableCell>
                      <TableCell>Solicitante</TableCell>
                      <TableCell>Motivo</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Fecha</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returns.map((ret) => (
                      <TableRow key={ret._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {ret.consecutivo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{ret.solicitudConsecutivo || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{ret.solicitante?.nombre}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>{ret.motivo}</Typography>
                        </TableCell>
                        <TableCell><EstadoChip estado={ret.estado} /></TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(ret.createdAt)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Filas"
              />
            </>
          )}
        </CardContent>
      </Card>

      <DevolucionFormModal open={openModal} onClose={() => setOpenModal(false)} onSubmit={handleCreate} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default DevolucionesPage;
